import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { lazy, StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import LSToastContainer from './layouts/components/LSToastContainer';
import MainProvider from './main/MainProvider';
import { createLogger } from './utils/logger';

const logger = createLogger('Bootstrap');

// Lazy load the Main component
const Main = lazy(() => import('./main/Main'));

/**
 * A component that displays the application's primary loading spinner.
 *
 * This component shows the brand clover spinner while the application is
 * initializing (Suspense fallback for the lazily-loaded Main component). A
 * `role="status"` with visually-hidden text announces the loading state to
 * assistive technology.
 *
 * @returns {JSX.Element} A centered div containing the brand loading spinner
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const InitialLoadingComponent = () => (
  <div
    className='position-relative'
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      flexDirection: 'column'
    }}>
    <span className='position-relative rounded-start' role='status'>
      <span className='ripple-large'>
        <span className='fa-spin position-absolute all-0 d-flex flex-center'>
          <span className='icon-spin position-absolute all-0 d-flex flex-center'>
            <FontAwesomeIcon
              icon='clover'
              className='text-primary'
              style={{ fontSize: '5em' }}
            />
          </span>
        </span>
      </span>
      <span className='visually-hidden'>Loading…</span>
    </span>
  </div>
);

/**
 * Initializes the React application.
 *
 * This function performs the following tasks:
 * 1. Gets the main container element from the DOM.
 * 2. Creates a React root in this container.
 * 3. Tests localStorage functionality by setting and removing a test item.
 * 4. Verifies and initializes the 'isFluid' setting in localStorage if it doesn't exist.
 * 5. Renders the main application structure including:
 *    - StrictMode for highlighting potential problems
 *    - MainProvider for context provision
 *    - Suspense with a loading component for code splitting
 *    - Main component as the core application
 *    - LSToastContainer for toast notifications
 *
 * @throws {Error} Logs an error if localStorage is not available
 * @returns {void}
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
export const initializeApp = () => {
  const container = document.getElementById('main');
  const root = createRoot(container);

  // Ensure localStorage is working before loading the app
  try {
    // Test if localStorage works
    localStorage.setItem('__test', 'test');
    localStorage.removeItem('__test');

    // Verify isFluid is properly stored
    const storedIsFluid = localStorage.getItem('isFluid');
    logger.debug('Initial localStorage test passed: isFluid =', storedIsFluid);

    // If isFluid isn't in localStorage yet, initialize it with default
    if (storedIsFluid === null) {
      // Import the default value - we're assuming true based on your ApplicationProvider
      localStorage.setItem('isFluid', JSON.stringify(true));
      logger.debug(
        'isFluid not found in localStorage, setting default value to true'
      );
    }
  } catch (e) {
    logger.error('LocalStorage is not available:', e);
  }

  root.render(
    <StrictMode>
      <MainProvider>
        <Suspense fallback={<InitialLoadingComponent />}>
          <Main />
        </Suspense>
        <LSToastContainer />
      </MainProvider>
    </StrictMode>
  );
};

// In development mode, we need to ensure the function is called if this file is
// loaded directly (e.g., during hot reloading)
if (process.env.NODE_ENV === 'development' && module.hot) {
  module.hot.accept();
}
